import type { RouteLocationRaw, Router } from 'vue-router';
import { useAssetsStore } from '@stores/assetsStore';

type Target = RouteLocationRaw | null | undefined;

/**
 * Navigate to a slow-loading page (the transcript editor) behind the global
 * opening overlay (components/OpeningOverlay.vue), shown from the click until
 * the target page settles and calls `assetsStore.clearOpening()`.
 *
 * - Repeat clicks while an open is in flight are ignored (returns false).
 * - `to` may be a function resolving the location (e.g. looking up the
 *   owning workspace first), so the spinner covers that wait too; a null
 *   location cancels the open.
 * - A navigation that does not happen (duplicated, aborted by a leave guard,
 *   cancelled by a newer navigation, or thrown) clears the overlay here,
 *   because no target page will mount to clear it. A successful navigation
 *   always remounts the target: the /Users router-view is keyed by fullPath.
 */
export async function openWithLoading(
  router: Router,
  to: Target | (() => Promise<Target>),
  label = '',
): Promise<boolean> {
  const assets = useAssetsStore();
  if (assets.openingFileName !== null) return false;
  assets.markOpening(label);
  try {
    const location = typeof to === 'function' ? await to() : to;
    if (!location) {
      assets.clearOpening();
      return false;
    }
    const failure = await router.push(location);
    if (failure) assets.clearOpening();
    return !failure;
  } catch {
    assets.clearOpening();
    return false;
  }
}
